import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronRight, Coins, Smartphone, Lock, BadgeCheck, ShieldCheck, Fingerprint, CreditCard, ChevronDown,
} from 'lucide-react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import AuroraBackground from '../components/AuroraBackground';
import GoldText from '../components/GoldText';
import StatusPill from '../components/StatusPill';
import Accordion from '../components/Accordion';
import { MODULES, MODULE_ICONS } from '../data/modules';

type Filter = 'All' | 'Live' | 'Coming Soon';

export default function LandingPage() {
  const [filter, setFilter] = useState<Filter>('All');
  const filteredModules = filter === 'All' ? MODULES : MODULES.filter((m) => m.status === filter);

  return (
    <div className="min-h-screen bg-bg">
      <SiteNav />

      {/* Hero */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <AuroraBackground />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 bg-gold/10 border border-gold/20 text-gold rounded-full text-sm font-semibold">
              <Coins size={14} /> Gold-Backed Financial Operating System
            </div>
            <h1 className="font-display text-5xl lg:text-6xl font-extrabold text-textPrimary mb-6 leading-[1.1]">
              Eighteen Modules.<br /><GoldText>One Gold-Backed Platform.</GoldText>
            </h1>
            <p className="text-xl text-textSecondary mb-4 leading-relaxed">
              Bankably tokenizes physical gold reserves into the Aureus Protocol — the base asset layer behind
              banking, credit building, fractional investing, crypto, and insurance, all in one place.
            </p>
            <p className="text-lg text-textTertiary mb-8">
              Bankably is mobile-first: you sign up and bank from the app. The web is where you can explore
              what's coming and join the waitlist.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/wishlist"
                className="px-8 py-3 bg-gold-gradient text-bg rounded-lg font-semibold hover:opacity-90 transition inline-flex items-center justify-center gap-2"
              >
                Join the Waitlist <ChevronRight size={20} />
              </Link>
              <a
                href="#modules"
                className="px-8 py-3 border border-border text-textPrimary rounded-lg font-semibold hover:border-gold/40 hover:bg-surface1 transition inline-flex items-center justify-center gap-2"
              >
                <Smartphone size={18} /> Explore the Modules
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="hidden lg:flex items-center justify-center"
          >
            <div className="relative w-80 h-80">
              <div className="absolute inset-0 rounded-full bg-gold-gradient opacity-20 blur-3xl" />
              <motion.div
                className="absolute inset-0 rounded-full border border-gold/20"
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
              >
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-gold" />
              </motion.div>
              <motion.div
                className="absolute inset-10 rounded-full border border-gold/15"
                animate={{ rotate: -360 }}
                transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
              >
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2.5 h-2.5 rounded-full bg-goldLight" />
              </motion.div>
              <div className="absolute inset-16 rounded-full bg-surface1 border border-gold/30 flex items-center justify-center shadow-2xl">
                <Coins size={88} className="text-gold" />
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="relative flex justify-center mt-8"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ChevronDown size={22} className="text-textTertiary" />
        </motion.div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-surface1/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-textPrimary text-center mb-16">How Bankably Works</h2>
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10">
            <motion.div
              className="hidden md:block absolute top-7 left-[16.5%] right-[16.5%] h-px bg-border origin-left"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: 'easeInOut' }}
            />
            {[
              { icon: <Smartphone className="text-gold" size={26} />, title: '1. Sign Up on Mobile', body: 'Bankably is mobile-first. Your account, KYC, and security all happen in the app — not yet publicly released.' },
              { icon: <Coins className="text-gold" size={26} />, title: '2. Backed by Gold', body: 'Every AUS token represents a unit of physical gold value, with a verifiable, audited collateral ratio behind it.' },
              { icon: <BadgeCheck className="text-gold" size={26} />, title: '3. Log In on Web', body: "Once you've signed up on mobile, your same account works here — for admins, that means a full dashboard." },
            ].map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="relative text-center"
              >
                <div className="relative z-10 w-14 h-14 mx-auto mb-4 rounded-2xl bg-surface1 border border-gold/20 flex items-center justify-center">
                  {step.icon}
                </div>
                <h3 className="font-bold text-textPrimary mb-2">{step.title}</h3>
                <p className="text-textSecondary text-sm leading-relaxed">{step.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules grid */}
      <section id="modules" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-textPrimary text-center mb-3">Every Module, One Account</h2>
          <p className="text-textSecondary text-center mb-8 max-w-2xl mx-auto">
            Each module is its own self-contained mini-app — independently subscribable, all built on the same
            gold-backed foundation.
          </p>

          <div className="flex justify-center gap-2 mb-12">
            {(['All', 'Live', 'Coming Soon'] as Filter[]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${
                  filter === f ? 'bg-gold-gradient text-bg' : 'bg-surface1 text-textSecondary border border-border hover:border-gold/30'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredModules.map((m, i) => {
              const Icon = MODULE_ICONS[m.icon] ?? MODULE_ICONS.Wallet;
              return (
                <motion.div
                  key={m.slug}
                  layout
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (i % 6) * 0.05 }}
                >
                  <Link
                    to={`/modules/${m.slug}`}
                    className="group block bg-surface1 rounded-xl p-5 border border-border hover:border-gold/40 hover:shadow-[0_0_30px_-10px_rgba(242,202,80,0.35)] hover:-translate-y-0.5 transition-all h-full"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-11 h-11 rounded-lg bg-gradient-to-br ${m.accent} flex items-center justify-center text-white`}>
                        <Icon size={20} />
                      </div>
                      <StatusPill status={m.status} />
                    </div>
                    <h3 className="font-bold text-textPrimary mb-1 group-hover:text-gold transition-colors">{m.name}</h3>
                    <p className="text-textSecondary text-sm">{m.tagline}</p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust & Security */}
      <section className="py-20 bg-surface1/40 border-y border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-textPrimary text-center mb-3">Built for Trust</h2>
          <p className="text-textSecondary text-center mb-12 max-w-2xl mx-auto">
            Security isn't a feature here — it's the foundation everything else is built on.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: <Fingerprint className="text-gold" size={24} />, title: 'Identity Verification', body: 'KYC handled through Didit’s verified identity infrastructure before any account goes live.' },
              { icon: <ShieldCheck className="text-gold" size={24} />, title: 'Two-Factor Authentication', body: 'TOTP-based 2FA available on every account, plus biometric unlock on mobile.' },
              { icon: <CreditCard className="text-gold" size={24} />, title: 'PCI-Compliant Payments', body: 'Card payments and payouts are processed through Stripe’s PCI DSS-certified infrastructure.' },
            ].map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-surface1 border border-border rounded-2xl p-6"
              >
                <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center mb-4">{f.icon}</div>
                <h3 className="font-bold text-textPrimary mb-2">{f.title}</h3>
                <p className="text-textSecondary text-sm leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Real pricing */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold text-textPrimary mb-3">Transparent, Published Pricing</h2>
          <p className="text-textSecondary mb-12">
            Most modules are free to use; some unlock with a subscription. Here's what's published so far —
            for Money From A Friend, our credit-building module for informal loans:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-xl mx-auto">
            <div className="border border-border bg-surface1 rounded-2xl p-6">
              <h3 className="font-bold text-textPrimary mb-1">Standard</h3>
              <p className="text-3xl font-bold text-textPrimary mb-3">$5<span className="text-base text-textTertiary">/mo</span></p>
              <p className="text-textSecondary text-sm">Basic credit recording and bureau reporting.</p>
            </div>
            <div className="border-2 border-gold rounded-2xl p-6 bg-surface1 relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wide bg-gold-gradient text-bg px-2 py-0.5 rounded-full">Popular</span>
              <h3 className="font-bold text-textPrimary mb-1">Premium</h3>
              <p className="text-3xl font-bold text-textPrimary mb-3">$15<span className="text-base text-textTertiary">/mo</span></p>
              <p className="text-textSecondary text-sm">Automated agreement templates, forgiveness tracking, detailed credit impact analytics.</p>
            </div>
          </div>
          <p className="text-textTertiary text-sm mt-8">Pricing for other modules will be published as they roll out publicly.</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-surface1/40 border-y border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-textPrimary text-center mb-12">Frequently Asked Questions</h2>
          <Accordion
            items={[
              { question: 'Why can’t I sign up on the website?', answer: 'Bankably is mobile-first. Account creation, KYC, and security all happen in the Bankably app, which isn’t publicly released yet. Join the waitlist and we’ll notify you the moment it is.' },
              { question: 'What does "gold-backed" actually mean?', answer: 'The Aureus Protocol tokenizes physical gold reserves into AUS, a base asset that underpins banking, credit, and investing modules across the platform.' },
              { question: 'Is my identity verification secure?', answer: 'Yes — identity verification runs through Didit’s verified KYC infrastructure, separate from your everyday account credentials.' },
              { question: 'Can I use the web app once I have a mobile account?', answer: 'Yes. The same account works on web. Admin accounts get a full dashboard; regular member access on web is still being built.' },
            ]}
          />
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden">
        <AuroraBackground variant="subtle" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold text-textPrimary mb-4">Be First In When We Launch</h2>
          <p className="text-textSecondary mb-8">
            The mobile app isn't public yet. Join the waitlist and we'll notify you the moment you can sign up.
          </p>
          <Link
            to="/wishlist"
            className="inline-flex items-center gap-2 px-8 py-3 bg-gold-gradient text-bg rounded-lg font-semibold hover:opacity-90 transition"
          >
            <Lock size={18} /> Join the Waitlist
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
